/**
 * Highcharts light-theme defaults.
 *
 * Highcharts 13 moved most structural colors (chart background, axis lines,
 * grid lines, ticks) onto CSS custom properties such as
 * `var(--highcharts-neutral-color-10)`, which resolve to dark values under
 * `prefers-color-scheme: dark` — and to an invalid SVG stroke/fill (i.e. black)
 * when undefined, which is our case since we run in non-styled mode and don't
 * import `highcharts/css/highcharts.css`.
 *
 * This restores the explicit light neutrals Highcharts 12 used by default, so
 * charts look the same regardless of the OS color scheme. Apply it on the
 * Highcharts singleton before creating any chart; `setOptions` mutates shared
 * defaults, so calling it more than once is harmless (guarded below anyway).
 *
 * Usage:
 *   import("highcharts").then((Highcharts) => {
 *     applyHighchartsTheme(Highcharts.default)
 *     Highcharts.default.chart(el, { ... })
 *   })
 */

let applied = false

export function applyHighchartsTheme(H) {
  if (applied) return
  applied = true

  // Light values for every structural color that Highcharts 13 defaults to an
  // undefined `var(--highcharts-…)` (and would otherwise render black here).
  // Text colors (axis labels, titles, data labels) are intentionally left
  // alone — black text is fine, and these are the only elements that draw as
  // dark *shapes*: backgrounds, grid lines, axis lines, ticks, the plot border,
  // and tooltips.
  H.setOptions({
    chart: {
      backgroundColor: "transparent",
      plotBorderColor: "#cccccc", // was var(--highcharts-neutral-color-20)
    },
    xAxis: {
      gridLineColor: "#e6e6e6",
      lineColor: "#ccd6eb",
      tickColor: "#ccd6eb",
      minorGridLineColor: "#f2f2f2",
      minorTickColor: "#cccccc",
    },
    yAxis: {
      gridLineColor: "#e6e6e6",
      lineColor: "#ccd6eb",
      tickColor: "#ccd6eb",
      minorGridLineColor: "#f2f2f2",
      minorTickColor: "#cccccc",
    },
    tooltip: {
      backgroundColor: "#ffffff", // was var(--highcharts-background-color)
      borderColor: "#cccccc",
    },
    plotOptions: {
      series: {
        // Point/slice separator border defaults to the (undefined) background
        // var; restore white so pie slices and stacked columns stay separated
        // instead of getting black outlines.
        borderColor: "#ffffff",
      },
    },
  })
}
